// src/services/adminApi.ts
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/functions";
import type { Role } from "../models/auth";
import type { UserProfile } from "../models/user";
import type { Course, CoursePatch as TCoursePatch } from "../models/course";
import type { QuestionPatch as TQuestionPatch } from "../models/question";

function clean<T extends Record<string, unknown>>(
  obj: T
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out;
}

// ── Usuarios ────────────────────────────────────────────────────────────────
export async function adminUpdateUserProfile(
  uid: string,
  patch: Partial<UserProfile>
): Promise<void> {
  const call = httpsCallable<
    { uid: string; patch: Record<string, unknown> },
    { ok: true }
  >(functions, "adminUpdateUserProfile");
  await call({ uid, patch: clean(patch as Record<string, unknown>) });
}

export async function adminSetUserRole(uid: string, role: Role): Promise<void> {
  const call = httpsCallable<{ uid: string; role: Role }, { ok: true }>(
    functions,
    "adminSetUserRole"
  );
  await call({ uid, role });
}

// ── Comunidad ───────────────────────────────────────────────────────────────
export async function adminDeleteCommunityPost(postId: string): Promise<void> {
  const call = httpsCallable<{ postId: string }, { ok: true }>(
    functions,
    "adminDeleteCommunityPost"
  );
  await call({ postId });
}

// ── Questions (solo text/helpText por ahora) ────────────────────────────────
export async function adminUpsertQuestion(
  qid: string,
  patch: TQuestionPatch
): Promise<void> {
  const call = httpsCallable<
    { qid: string; data: Record<string, unknown> },
    { ok: true }
  >(functions, "adminUpsertQuestion");
  await call({
    qid,
    data: clean({ text: patch.text, helpText: patch.helpText }),
  });
}

export async function adminDeleteQuestion(qid: string): Promise<void> {
  const call = httpsCallable<{ qid: string }, { ok: true }>(
    functions,
    "adminDeleteQuestion"
  );
  await call({ qid });
}

// ── Certificaciones ─────────────────────────────────────────────────────────
export async function adminUpsertCertification(
  certId: string,
  data: Record<string, unknown>
): Promise<void> {
  const call = httpsCallable<
    { certId: string; data: Record<string, unknown> },
    { ok: true }
  >(functions, "adminUpsertCertification");
  await call({ certId, data: clean(data) });
}

export async function adminDeleteCertification(certId: string): Promise<void> {
  const call = httpsCallable<{ certId: string }, { ok: true }>(
    functions,
    "adminDeleteCertification"
  );
  await call({ certId });
}

// ── Storage ─────────────────────────────────────────────────────────────────
export async function adminCreateUploadUrl(
  path: string,
  contentType: string
): Promise<string> {
  const call = httpsCallable<
    { path: string; contentType: string },
    { url: string }
  >(functions, "adminCreateUploadUrl");
  return (await call({ path, contentType })).data.url;
}

export async function adminDeleteFile(path: string): Promise<void> {
  const call = httpsCallable<{ path: string }, { ok: true }>(
    functions,
    "adminDeleteFile"
  );
  await call({ path });
}

// ── Cursos ──────────────────────────────────────────────────────────────────
export type CoursesCursor = { createdAt: string; id: string };

export async function adminListCourses(params?: {
  limit?: number;
  cursor?: CoursesCursor;
}): Promise<{ items: Course[]; nextCursor: CoursesCursor | null }> {
  const call = httpsCallable<
    { limit?: number; cursor?: CoursesCursor } | undefined,
    { items: Course[]; nextCursor: CoursesCursor | null }
  >(functions, "adminListCourses");

  const p =
    params && (params.limit !== undefined || params.cursor !== undefined)
      ? {
          ...(params.limit !== undefined ? { limit: params.limit } : {}),
          ...(params.cursor ? { cursor: params.cursor } : {}),
        }
      : undefined;

  return (await call(p)).data;
}

export async function adminUpsertCourse(
  courseId: string,
  data: TCoursePatch
): Promise<void> {
  const call = httpsCallable<
    { courseId: string; data: Record<string, unknown> },
    { ok: true }
  >(functions, "adminUpsertCourse");

  await call({
    courseId,
    data: clean({
      title: data.title,
      description: data.description,
      coverURL: data.coverURL,
      level: data.level,
      visibility: data.visibility,
      tags: data.tags,
      courseURL: data.courseURL, // ✅ incluir el nuevo campo para que no se descarte
    }),
  });
}

export async function adminDeleteCourse(courseId: string): Promise<void> {
  const call = httpsCallable<{ courseId: string }, { ok: true }>(
    functions,
    "adminDeleteCourse"
  );
  await call({ courseId });
}
